
export enum UserRole {
  PROVIDER = 'PROVIDER',
  RECEIVER = 'RECEIVER'
}

export enum ProjectStatus {
  DRAFT = 'DRAFT', // Pre-launch / Onboarding
  ACTIVE = 'ACTIVE',
  WAITING_ON_RECEIVER = 'WAITING_ON_RECEIVER', // Ball in receiver's court
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED'
}

export enum MilestoneStatus {
  DRAFT = 'DRAFT', // New draft status
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  REVIEW = 'REVIEW', // Provider has delivered, waiting for receiver
  APPROVED = 'APPROVED' // Locked and completed
}

export enum PresenceStatus {
  ACTIVE = 'ACTIVE',
  FOCUSED = 'FOCUSED',
  AWAY = 'AWAY',
  OFFLINE = 'OFFLINE'
}

export type AssigneeVisibility = 'HIDDEN' | 'LEAD' | 'MILESTONE';

export interface Workspace {
  id: string;
  name: string;
  role: UserRole; // The role the current user has in this workspace
  initials: string;
}

export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  role: string;
  email?: string;
  status?: 'Active' | 'Pending';
  presence: PresenceStatus;
}

export interface Attachment {
  id: string;
  name: string;
  type: 'image' | 'file' | 'link';
  url?: string;
  size?: string;
  uploadedAt?: string;
  uploadedBy?: string;
  source?: 'comment' | 'project';
}

export interface Note {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  author: string;
  authorRole: UserRole;
}

export interface Comment {
  id: string;
  author: string;
  role: UserRole;
  text: string;
  date: string;
  isResolved: boolean;
  isDelivery?: boolean;
  isEdited?: boolean;
  isDeleted?: boolean;
  attachments?: Attachment[];
}

export interface Milestone {
  id: string;
  title: string;
  description?: string;
  status: MilestoneStatus;
  duration: number;
  completedAt?: string;
  assigneeId?: string;
  comments: Comment[];
}

export interface OnboardingQuestion {
  id: string;
  text: string;
  required: boolean;
}

export interface OnboardingAnswer {
  questionId: string;
  questionText: string;
  answerText: string;
}

export interface Project {
  id: string;
  workspaceId: string;
  name: string;
  receiverName: string;
  receiverEmail: string;
  status: ProjectStatus;
  startDate?: string;
  milestones: Milestone[];
  updatedAt: string;
  description?: string;
  assigneeVisibility: AssigneeVisibility;
  onboardingQuestions?: OnboardingQuestion[];
  onboardingAnswers?: OnboardingAnswer[];
  notes: Note[];
  files: Attachment[];
}

export interface Notification {
  id: string;
  type: 'approval' | 'feedback' | 'system';
  message: string;
  projectId: string;
  isRead: boolean;
  date: string;
}

export type PersonRole = 'Provider' | 'Receiver' | 'Collaborator';
export type RelationshipStatus = 'Active' | 'Invited' | 'Past';

export interface Person {
  id: string;
  name: string;
  email: string;
  initials: string;
  avatarColor: string;
  company?: string;
  role: PersonRole;
  relationshipStatus: RelationshipStatus;
  presence?: PresenceStatus;
  sharedProjectsCount?: number;
}